import { Chip } from '@mantine/core'
import { Button } from '@pubstack/ui/components/Button'
import { GlobalModalWrapper } from '@pubstack/ui/components/GlobalModalWrapper'
import { Input } from '@pubstack/ui/components/Input'
import { MultiSelectInput } from '@pubstack/ui/components/MultiSelectInput'
import { Actions, Body, Title } from '@pubstack/ui/components/ModalLayout'
import Table, { Column, handleTableSearchAndSort, onColumnSort, SortableColumn, TableColumns } from '@pubstack/ui/components/table/Table'
import { TableCell } from '@pubstack/ui/components/table/TableCell'
import { TableRow } from '@pubstack/ui/components/table/TableRow'
import { cn } from '@pubstack/ui/utils/cn'
import { toDateTime } from '@pubstack/ui/utils/dates'
import { FunctionComponent, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { ServiceApiKey, useCreateServiceApiKey, useOwnerScopes, useRevokeServiceApiKey, useServiceApiKeys } from '~/api/app/api-keys.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { useGlobalModal } from '~/components/layout/GlobalModal'

type NewServiceKeyForm = { name: string; scopeIds: string[] }

const SecretKeyModal: FunctionComponent<{ secret: string; name: string }> = ({ secret, name }) => {
  const { close } = useGlobalModal()
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  return (
    <GlobalModalWrapper>
      <Title>API key created</Title>
      <Body className="flex flex-col gap-3">
        <p>
          Copy the key <strong>{name}</strong> now. It will not be shown again.
        </p>
        {/* ph-no-capture: PostHog session replay and autocapture must never record the plaintext key */}
        <code className="ph-no-capture break-all rounded border border-solid p-2 select-all">{secret}</code>
        <div>
          <Button variant="secondary" onClick={copy} trackingLabel="settings-service-api-keys-secret-copy">
            {copied ? 'Copied' : 'Copy key'}
          </Button>
        </div>
      </Body>
      <Actions>
        <Button onClick={close} trackingLabel="settings-service-api-keys-secret-close">
          Done
        </Button>
      </Actions>
    </GlobalModalWrapper>
  )
}

const NewServiceKeyModal: FunctionComponent = () => {
  const modal = useGlobalModal()
  const { currentScopeId } = useAuthenticatedUser()
  const { control, handleSubmit, formState, setValue } = useForm<NewServiceKeyForm>({ defaultValues: { name: '', scopeIds: [currentScopeId] } })
  const { ownerScopes } = useOwnerScopes()
  const scopeOptions = ownerScopes.map((scope) => ({ value: scope.id, label: scope.name, trackingLabel: 'settings-service-api-keys-scopes-set' as const, disabled: scope.id === currentScopeId }))
  // the current scope is always part of the key: it cannot be removed, whatever the control does
  const withCurrentScope = (scopeIds: string[]) => (scopeIds.includes(currentScopeId) ? scopeIds : [currentScopeId, ...scopeIds])
  // with a single owner scope there is nothing to pick: the key is for the current one
  const canPickScopes = ownerScopes.length > 1
  const { mutateAsync: createKey, isPending } = useCreateServiceApiKey({ errorToast: 'Error on creating service API key' })

  const onSubmit = async ({ name, scopeIds }: NewServiceKeyForm) => {
    const created = await createKey({ name: name.trim(), scopeIds: canPickScopes ? withCurrentScope(scopeIds) : [currentScopeId] }).catch(() => undefined)
    // on failure the mutation already showed the error toast: keep the form open
    if (!created) return
    await modal.close()
    // the key is shown once: only the Done button closes this modal
    modal.open(SecretKeyModal, { secret: created.key, name: created.apiKey.name }, { shouldCloseOnOverlayClick: false, shouldCloseOnEsc: false, showCloseButton: false })
  }

  return (
    <GlobalModalWrapper>
      <Title>Add service API key</Title>
      <Body className="flex flex-col gap-3">
        <Input name="name" rules={{ required: true, maxLength: 100 }} labelIsPlaceholder control={control} label="API key name" />
        {canPickScopes && (
          <>
            <MultiSelectInput
              name="scopeIds"
              control={control}
              rules={{ validate: (scopeIds) => (scopeIds ?? []).length > 0 || 'Select at least one scope' }}
              error={formState.errors.scopeIds?.message}
              selectableOptions={scopeOptions}
              label="Scopes"
              labelIsPlaceholder
              entityLabel="scopes"
              displayLabel
              trackingLabel="settings-service-api-keys-scopes-set"
              onChange={(next) => {
                const withCurrent = withCurrentScope(next)
                if (withCurrent.length !== next.length) setValue('scopeIds', withCurrent, { shouldValidate: true })
              }}
            />
            <p className="text-sm opacity-70">The current scope is always included.</p>
          </>
        )}
      </Body>
      <Actions>
        <Button variant="tertiary" onClick={modal.close} trackingLabel="settings-service-api-keys-creation-cancel">
          Cancel
        </Button>
        <Button disabled={isPending} onClick={handleSubmit(onSubmit)} trackingLabel="settings-service-api-keys-creation-confirm">
          Create key
        </Button>
      </Actions>
    </GlobalModalWrapper>
  )
}

const RevokeServiceKeyModal: FunctionComponent<{ apiKey: ServiceApiKey }> = ({ apiKey }) => {
  const { close } = useGlobalModal()
  const { mutateAsync: revoke, isPending } = useRevokeServiceApiKey({
    errorToast: 'Error on revoking service API key',
    successToast: (
      <>
        <strong>{apiKey.name}</strong> was revoked
      </>
    ),
  })

  return (
    <GlobalModalWrapper>
      <Title>Revoke API key</Title>
      <Body className="flex flex-col gap-2">
        <p>
          You are about to revoke <span className="font-bold">{apiKey.name}</span>.
        </p>
        {apiKey.otherScopesCount > 0 && (
          <p>
            This key is also used on {apiKey.otherScopesCount} other {apiKey.otherScopesCount === 1 ? 'scope' : 'scopes'}. Revoking it revokes it there too.
          </p>
        )}
        <p>Every integration using this key will stop working immediately. This cannot be undone.</p>
      </Body>
      <Actions>
        <Button variant="tertiary" onClick={close} trackingLabel="settings-service-api-keys-revoke-cancel">
          Cancel
        </Button>
        <Button
          variant="negative"
          disabled={isPending}
          onClick={async () => {
            try {
              await revoke({ id: apiKey.id })
            } catch {
              // the mutation already showed the error toast
            } finally {
              close()
            }
          }}
          trackingLabel="settings-service-api-keys-revoke-confirm"
        >
          Revoke key
        </Button>
      </Actions>
    </GlobalModalWrapper>
  )
}

const COLUMNS: (Column | SortableColumn<ServiceApiKey>)[] = [
  { name: 'Name', isSortable: true, order: 'ascending', attributeSort: 'name' },
  { name: 'Key', isSortable: false },
  { name: 'Scopes', isSortable: false },
  { name: 'Status', isSortable: true, order: 'none', attributeSort: 'status' },
  { name: 'Created', isSortable: true, order: 'none', attributeSort: 'createdAt' },
  { name: 'Created by', isSortable: true, order: 'none', attributeSort: 'createdBy' },
  { name: 'Last used', isSortable: true, order: 'none', attributeSort: 'lastUsedAt' },
  { name: '', isSortable: false },
]

const STATUS_FILTERS = [
  { value: 'all', label: 'All', trackingLabel: 'settings-service-api-keys-status-all-apply' },
  { value: 'active', label: 'Active', trackingLabel: 'settings-service-api-keys-status-active-apply' },
  { value: 'revoked', label: 'Revoked', trackingLabel: 'settings-service-api-keys-status-revoked-apply' },
] as const

type StatusFilter = (typeof STATUS_FILTERS)[number]['value']

const isStatusFilter = (value: string): value is StatusFilter => STATUS_FILTERS.some((filter) => filter.value === value)

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '-')
const formatDateTime = (iso: string) => toDateTime(iso).toFormat('dd LLL. yyyy, HH:mm')

export const ServiceApiKeysTab: FunctionComponent = () => {
  const { data: apiKeys, isFetching } = useServiceApiKeys()
  const { control } = useForm<{ name: string }>({ defaultValues: { name: '' } })
  const search = useWatch({ control, name: 'name' })
  const modal = useGlobalModal()
  const [columns, setColumns] = useState<TableColumns<ServiceApiKey>>(COLUMNS)

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')

  const filteredByStatus = (apiKeys || []).filter((apiKey) => statusFilter === 'all' || apiKey.status === statusFilter)
  const displayedKeys = handleTableSearchAndSort(columns, filteredByStatus, search, ['name'])

  const createButton = (
    <Button onClick={() => modal.open(NewServiceKeyModal, {})} trackingLabel="settings-service-api-keys-new">
      New service key
    </Button>
  )

  return (
    <div className="flex flex-col gap-3 pb-2.5">
      <div className="flex items-center gap-5">
        <Input iconLeft="search" name="name" control={control} label="Search" labelIsPlaceholder />
        {createButton}
        <Chip.Group multiple={false} value={statusFilter} onChange={(value) => isStatusFilter(value) && setStatusFilter(value)}>
          <div className={'flex flex-wrap gap-2'}>
            {STATUS_FILTERS.map(({ value, label, trackingLabel }) => (
              <Chip key={value} value={value} variant={'outline'} size={'sm'} data-tracking={trackingLabel}>
                {label}
              </Chip>
            ))}
          </div>
        </Chip.Group>
      </div>
      <Table columns={columns} headersTop={0} isLoading={isFetching} onClickHeading={(column) => onColumnSort(columns, column, setColumns)}>
        {displayedKeys.length ? (
          displayedKeys.map((apiKey) => (
            <TableRow key={apiKey.id}>
              <TableCell>{apiKey.name}</TableCell>
              <TableCell>
                <code>
                  {apiKey.prefix}…{apiKey.last4}
                </code>
              </TableCell>
              <TableCell>
                <div className="flex flex-col">
                  {apiKey.scopes.map((scope) => (
                    <span key={scope.id}>{scope.name}</span>
                  ))}
                </div>
              </TableCell>
              <TableCell>
                <span className={cn('capitalize', apiKey.status !== 'active' && 'opacity-70')}>{apiKey.status}</span>
              </TableCell>
              <TableCell>{formatDate(apiKey.createdAt)}</TableCell>
              <TableCell>{apiKey.createdBy}</TableCell>
              <TableCell>{apiKey.lastUsedAt ? formatDateTime(apiKey.lastUsedAt) : 'Not used'}</TableCell>
              <TableCell>
                {apiKey.status === 'active' && (
                  <Button variant="tertiary" onClick={() => modal.open(RevokeServiceKeyModal, { apiKey })} trackingLabel="settings-service-api-keys-revoke-request">
                    Revoke
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colspan={columns.length}>
              <div className="flex flex-col items-center gap-4 py-8 text-center">
                <div>
                  <h2>No service API key</h2>
                  <p>You do not have any service API keys yet.</p>
                </div>
                {createButton}
              </div>
            </TableCell>
          </TableRow>
        )}
      </Table>
    </div>
  )
}
